<template>
  <section>
    <BaseMarkdownCtx ref="markdown" class="app-markdown-content" :content="formatHtml" />
    <footer v-show="pre || next" class="app-markdown-footer">
      <NuxtLink
        :class="['app-markdown-link app-markdown-link--prev', pre ? 'visible' : 'invisible']"
        :to="`${prefixPath}/${pre?.url}`"
      >
        <IconsArrow class="app-markdown-arrow app-markdown-arrow--prev" />
        <span class="app-markdown-line-title app-markdown-line-title--desktop">
          {{ pre?.title }}
        </span>
        <span class="app-markdown-line-title app-markdown-line-title--mobile">
          {{ formatTransify('UGC_OFFICIAL_WEBSITE_DOCUMENT_LAST') }}
        </span>
      </NuxtLink>
      <NuxtLink
        :class="['app-markdown-link app-markdown-link--next', next ? 'visible' : 'invisible']"
        :to="`${prefixPath}/${next?.url}`"
      >
        <IconsArrow class="app-markdown-arrow app-markdown-arrow--next" />
        <span class="app-markdown-line-title app-markdown-line-title--desktop">
          {{ next?.title }}
        </span>
        <span class="app-markdown-line-title app-markdown-line-title--mobile">
          {{ formatTransify('UGC_OFFICIAL_WEBSITE_DOCUMENT_NEXT') }}
        </span>
      </NuxtLink>
    </footer>
  </section>
</template>
<script lang="ts" setup>
import { CatalogsConfig } from '~/components/gfr/types/components'

const props = defineProps<{
  id?: number
  content?: string
  prefixPath?: string
  next?: CatalogsConfig | null
  pre?: CatalogsConfig | null
  levels?: number[]
}>()

const store = useStore()
const { formatTransify } = store
const { $markdown } = useNuxtApp()
const formatMarkdown = computed(() => {
  return props.content
    ?.replace(/```golang([\s\S]*?)```/g, function (_, p1) {
      return '```go' + p1 + '\n```'
    }) // markdown-it 不支持加粗、斜体、斜体加粗的结束符前有特殊字符，会导致解析错误，为避免此类情况，判断此类语法直接转化成html
    .replace(/(\*\*\*|\*\*|\*)(.*?)(\*\*\*|\*\*|\*)/g, function (_: any, p1: string, p2) {
      if (p1 === '***') {
        return `<strong><em>${p2}</em></strong>`
      }
      if (p1 === '**') {
        return `<strong>${p2}</strong>`
      }
      return `<em>${p2}</em>`
    })
})

const formatHtml = ref('')
const isRendering = ref(false)

watch(
  [formatMarkdown, () => props.levels],
  async ([markdown, levels]) => {
    if (!markdown) {
      formatHtml.value = ''
      return
    }

    isRendering.value = true
    try {
      const html = await $markdown.renderAsync(markdown, levels)
      formatHtml.value = html.replace(/(<table[^>]*>([\s\S]*?)<\/table>)/g, (_: any, p1: string) => {
        return `<div class="app-markdown-table">${p1}</div>`
      })
    } catch (error) {
      console.error('Markdown render error:', error)
      formatHtml.value = ''
    } finally {
      isRendering.value = false
    }
  },
  { immediate: true }
)
</script>
<style lang="scss">
@import '@/assets/css/markdown-theme.scss';
</style>
<style lang="scss" scoped>
.app-markdown-footer {
  @apply mt-16 flex h-7.5 justify-between max-lg:h-9;
}

.app-markdown-link {
  @apply flex max-w-[48%] items-center text-m-p text-white transition-colors duration-200 hover:text-primary;

  &--prev {
    @apply flex;
  }

  &--next {
    @apply flex-row-reverse justify-end;
  }
}

.app-markdown-arrow {
  @apply h-3.5 w-3.5 flex-shrink-0 max-lg:h-4 max-lg:w-4;

  &--prev {
    @apply rotate-90 ltr:mr-1 rtl:ml-1;
  }

  &--next {
    @apply -rotate-90 ltr:ml-1 rtl:mr-1;
  }
}

.app-markdown-line-title {
  @apply truncate capitalize;
  &--desktop {
    @apply hidden max-lg:text-2xl lg:block;
  }

  &--mobile {
    @apply hidden max-lg:block max-lg:text-2xl;
  }
}
</style>
